import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { INR, fmtDate, titleCase } from "@/lib/format";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Award, Download, Loader2 } from "lucide-react";
import { TargetsPanel } from "@/components/SalesTargets";
import { DatePicker, DateTimePicker } from "@/components/DatePicker";

const PART = { closed: "Closed sale", lead: "Brought lead", called: "Called" };

export function SalesListDialog({ user, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => { if (user) api.get(`/users/${user.user_id || user.id}/sales`).then(({ data }) => setItems(data)).catch(() => setItems([])); }, [user]);
  if (!user) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-2xl max-h-[85vh] overflow-y-auto rounded-xl" data-testid="user-sales-dialog">
        <DialogHeader><DialogTitle>Sales · {user.name}</DialogTitle></DialogHeader>
        {!items ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : items.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No sales yet</p> : (
          <div className="space-y-2">{items.map((s) => (
            <div key={s.type + s.ref} className="border rounded-lg p-3 text-sm" data-testid="user-sale-row">
              <div className="flex justify-between gap-2"><span className="font-mono text-xs">{s.ref}</span><span className="font-semibold tabular">{INR(s.amount)}</span></div>
              <div className="font-semibold">{s.customer}</div>
              <div className="text-xs text-muted-foreground">{titleCase(s.type)} · {s.detail} · {fmtDate(s.date)} · closed by {s.closed_by || "—"}</div>
              <div className="flex gap-1 mt-1">{s.my_part.map((p) => <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-semibold">{PART[p]}</span>)}</div>
            </div>))}</div>)}
      </DialogContent>
    </Dialog>
  );
}

export default function EmployeeSales() {
  const [range, setRange] = useState({ start: "", end: "" });
  const [rows, setRows] = useState(null);
  const [pick, setPick] = useState(null);
  useEffect(() => { setRows(null); api.get("/reports/employee-sales", { params: { start: range.start || undefined, end: range.end || undefined } }).then(({ data }) => setRows(data)).catch(() => setRows([])); }, [range]);
  const csv = async () => {
    const { data } = await api.get("/reports/employee-sales", { params: { ...range, format: "csv" }, responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = "employee-sales.csv"; a.click();
  };
  return (
    <div>
      <PageHeader title="Employee Sales" subtitle="Who closed each sale, who brought the lead and who called. Saved permanently at the time of sale." icon={Award}
        actions={<Button variant="outline" onClick={csv} data-testid="employee-sales-csv-btn"><Download className="h-4 w-4 mr-1.5" />Excel (CSV)</Button>} />
      <TargetsPanel />
      <div className="font-bold mb-2">All sales</div>
      <div className="flex gap-2 mb-4 flex-wrap">
        <label className="text-xs text-muted-foreground">From<DatePicker className="w-48 mt-1" value={range.start} onChange={(x) => setRange({ ...range, start: x })} testId="employee-sales-start" /></label>
        <label className="text-xs text-muted-foreground">To<DatePicker className="w-48 mt-1" value={range.end} onChange={(x) => setRange({ ...range, end: x })} testId="employee-sales-end" /></label>
        {(range.start || range.end) && <Button variant="ghost" size="sm" className="self-end" onClick={() => setRange({ start: "", end: "" })} data-testid="employee-sales-clear">Clear</Button>}
      </div>
      {!rows ? <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" /> : rows.length === 0 ? <Card className="p-10 text-center text-sm text-muted-foreground">No sales recorded in this period</Card> : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{rows.map((r) => (
          <Card key={r.user_id} className="p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setPick(r)} data-testid="employee-sales-card">
            <div className="font-bold">{r.name}</div><div className="text-xs text-muted-foreground font-mono">{r.employee_id} · {titleCase(r.role)}</div>
            <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
              <div><div className="text-xs text-muted-foreground">Policies closed</div><b>{r.policies_closed}</b> · {INR(r.premium_closed)}</div>
              <div><div className="text-xs text-muted-foreground">Loans disbursed</div><b>{r.loans_closed}</b> · {INR(r.disbursed_closed)}</div>
              <div><div className="text-xs text-muted-foreground">Leads brought</div><b>{r.leads_sourced}</b></div>
              <div><div className="text-xs text-muted-foreground">Sales where called</div><b>{r.sales_called}</b></div>
            </div>
          </Card>))}</div>)}
      <SalesListDialog user={pick} onClose={() => setPick(null)} />
    </div>
  );
}
